import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { MapPin, User, Mail, Phone, Globe, Users } from "lucide-react";
import type { Venue } from "@shared/schema";

interface VenueFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organisationId: number;
  venue?: Venue;
  onVenueCreated?: (venue: Venue) => void; // Callback for when a venue is created
}

export function VenueFormModal({ 
  open, 
  onOpenChange, 
  organisationId, 
  venue,
  onVenueCreated 
}: VenueFormModalProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    country: "",
    contactName: "",
    contactEmail: "",
    contactPhone: "",
    website: "",
    capacity: "",
    notes: "",
    active: true,
  });

  // Load existing venue data if editing
  useEffect(() => {
    if (venue) {
      setFormData({
        name: venue.name,
        address: venue.address,
        city: venue.city || "",
        state: venue.state || "",
        zipCode: venue.zipCode || "",
        country: venue.country || "",
        contactName: venue.contactName || "",
        contactEmail: venue.contactEmail || "",
        contactPhone: venue.contactPhone || "",
        website: venue.website || "",
        capacity: venue.capacity?.toString() || "",
        notes: venue.notes || "",
        active: venue.active,
      });
    } else {
      // Reset form for new venue
      setFormData({
        name: "",
        address: "",
        city: "",
        state: "",
        zipCode: "",
        country: "",
        contactName: "",
        contactEmail: "",
        contactPhone: "",
        website: "",
        capacity: "",
        notes: "",
        active: true,
      });
    }
  }, [venue]);

  // Create/update venue mutation
  const mutation = useMutation({
    mutationFn: async (data: any) => {
      const url = venue 
        ? `/api/venues/${venue.id}`
        : `/api/venues?organisationId=${organisationId}`;
      
      const response = await fetch(url, {
        method: venue ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to save venue");
      }

      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [`/api/venues`] });
      toast({
        title: venue ? "Venue Updated" : "Venue Created",
        description: venue ? "Venue details have been updated." : "New venue has been created.",
      });
      
      // Call the callback if provided (for creating a venue during event creation)
      if (!venue && onVenueCreated) {
        onVenueCreated(data);
      }
      
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleSubmit = () => {
    if (!formData.name || !formData.address) {
      toast({
        title: "Missing Required Fields",
        description: "Please fill in venue name and address.",
        variant: "destructive",
      });
      return;
    }

    const data: any = {
      name: formData.name,
      address: formData.address,
      city: formData.city || undefined,
      state: formData.state || undefined,
      zipCode: formData.zipCode || undefined,
      country: formData.country || undefined,
      contactName: formData.contactName || undefined,
      contactEmail: formData.contactEmail || undefined,
      contactPhone: formData.contactPhone || undefined,
      website: formData.website || undefined,
      capacity: formData.capacity ? parseInt(formData.capacity) : undefined,
      notes: formData.notes || undefined,
      active: formData.active,
    };

    mutation.mutate(data);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{venue ? "Edit Venue" : "Create Venue"}</DialogTitle>
          <DialogDescription>
            {venue ? "Update the venue details." : "Add a new venue for hosting events."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Basic Information */}
          <div>
            <Label htmlFor="name">
              Venue Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Madison Square Garden"
            />
          </div>

          {/* Address Fields */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="address">
                <MapPin className="inline h-4 w-4 mr-1" />
                Street Address <span className="text-red-500">*</span>
              </Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                placeholder="4 Pennsylvania Plaza"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                  placeholder="New York"
                />
              </div>
              <div>
                <Label htmlFor="state">State/Province</Label>
                <Input
                  id="state"
                  value={formData.state}
                  onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value }))}
                  placeholder="NY"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="zipCode">ZIP/Postal Code</Label>
                <Input
                  id="zipCode"
                  value={formData.zipCode}
                  onChange={(e) => setFormData(prev => ({ ...prev, zipCode: e.target.value }))}
                  placeholder="10001"
                />
              </div>
              <div>
                <Label htmlFor="country">Country</Label>
                <Input
                  id="country"
                  value={formData.country}
                  onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))}
                  placeholder="USA"
                />
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="space-y-4 pt-4 border-t">
            <h3 className="font-medium">Venue Contact Information</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="contactName">
                  <User className="inline h-4 w-4 mr-1" />
                  Contact Name
                </Label>
                <Input
                  id="contactName"
                  value={formData.contactName}
                  onChange={(e) => setFormData(prev => ({ ...prev, contactName: e.target.value }))}
                  placeholder="John Smith"
                />
              </div>
              <div>
                <Label htmlFor="contactPhone">
                  <Phone className="inline h-4 w-4 mr-1" />
                  Contact Phone
                </Label>
                <Input
                  id="contactPhone"
                  value={formData.contactPhone}
                  onChange={(e) => setFormData(prev => ({ ...prev, contactPhone: e.target.value }))}
                  placeholder="(212) 465-6741"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="contactEmail">
                  <Mail className="inline h-4 w-4 mr-1" />
                  Contact Email
                </Label>
                <Input
                  id="contactEmail"
                  type="email"
                  value={formData.contactEmail}
                  onChange={(e) => setFormData(prev => ({ ...prev, contactEmail: e.target.value }))}
                  placeholder="contact@venue.com"
                />
              </div>
              <div>
                <Label htmlFor="website">
                  <Globe className="inline h-4 w-4 mr-1" />
                  Website
                </Label>
                <Input
                  id="website"
                  type="url"
                  value={formData.website}
                  onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                  placeholder="https://www.venue.com"
                />
              </div>
            </div>
          </div>

          {/* Additional Details */}
          <div>
            <Label htmlFor="capacity">
              <Users className="inline h-4 w-4 mr-1" />
              Capacity
            </Label>
            <Input
              id="capacity"
              type="number"
              value={formData.capacity}
              onChange={(e) => setFormData(prev => ({ ...prev, capacity: e.target.value }))}
              placeholder="500"
            />
          </div>

          <div>
            <Label htmlFor="notes">Internal Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              rows={3}
              placeholder="Any additional notes about this venue..."
            />
          </div>

          {venue && (
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="active"
                checked={formData.active}
                onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.checked }))}
                className="h-4 w-4"
              />
              <Label htmlFor="active" className="cursor-pointer">
                Active (venue is available for bookings)
              </Label>
            </div>
          )}
        </div>

        <DialogFooter className="flex-col-reverse sm:flex-row gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="w-full sm:w-auto">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={mutation.isPending}
            className="w-full sm:w-auto"
          >
            {mutation.isPending ? "Saving..." : venue ? "Update Venue" : "Create Venue"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}